import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { RefreshCw } from 'lucide-react';
import { Button } from 'components/ui/button';
import UIOtpInput from 'components/core/otp-input/otp-input';
import emailSentIcon from 'assets/images/email_sent.svg';
import { useSigninMutation } from 'features/auth/hooks/use-auth';
import { useAuthStore } from 'state/store/auth/useAuthStore';
import { useToast } from 'hooks/use-toast';
import { MFASigninResponse } from 'features/auth/services/auth-service';
import { UserMfaType } from 'features/profile/enums/user-mfa-type-enum';
import useResendOTPTime from 'hooks/use-resend-otp';
import { useResendOtp } from 'features/profile/hooks/use-mfa';
import { useTranslation } from 'react-i18next';
import { useConfig } from 'hooks/use-config';
import { completeSigninWithOrganization } from 'features/auth/services/auth-service';
import { useLanguageContext } from 'i18n/language-context';
import { AuthPanelLoader } from 'pages/auth/shared/auth-panel-loader';
import { cn } from 'lib/utils';

const VERIFY_PANEL_LOADER_MS = 1000;

const formatResendCountdown = (seconds: number) => {
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
};

/**
 * Inline MFA code entry shown in AuthLayout (via /verify-key) when login returns
 * `mfa_enabled` / `mfa_id`. Styled like the profile email-verification dialog, but
 * not a modal — it replaces the login content.
 */
export function VerifyOtpKey() {
  const { t } = useTranslation();
  const { login, setSelectedOrgId } = useAuthStore();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [otpError, setOtpError] = useState('');
  const [otpValue, setOtpValue] = useState('');
  const { mutateAsync, isPending } = useSigninMutation();
  const { mutate: resendOtp } = useResendOtp();
  const [searchParams] = useSearchParams();
  const lastVerifiedOtpRef = useRef<string>('');
  const [newMfaId, setNewMfaId] = useState<string | null>(null);
  const { configurationSettings } = useConfig();
  const { isRouteLoading } = useLanguageContext();
  const [isContentReady, setIsContentReady] = useState(false);
  const mountedAtRef = useRef(Date.now());

  const mfaId = searchParams.get('mfa_id');
  const mfaType = Number(searchParams.get('mfa_type') || UserMfaType.EMAIL_VERIFICATION);
  const userEmail = searchParams.get('user_name');
  const isEmailMfa = mfaType === UserMfaType.EMAIL_VERIFICATION;
  const otpLength = mfaType === UserMfaType.AUTHENTICATOR_APP ? 6 : 5;

  const {
    isResendDisabled,
    remainingTime,
    handleResend: handleResendOTP,
  } = useResendOTPTime({
    initialTime: 120,
    onResend: () => {
      if (!mfaId) return;
      resendOtp(mfaId, {
        onSuccess: (data) => {
          if (data?.mfaId) {
            setNewMfaId(data.mfaId);
          }
        },
      });
    },
  });

  const onVerify = useCallback(async () => {
    try {
      const res = (await mutateAsync(
        {
          grantType: 'mfa_code',
          code: otpValue,
          mfaId: newMfaId ?? mfaId ?? '',
          mfaType: mfaType,
        },
        {
          onSuccess: () => {
            toast({
              variant: 'success',
              title: t('SUCCESS'),
              description: t('LOGIN_SUCCESSFULLY', 'You are successfully logged in'),
            });
          },
        }
      )) as MFASigninResponse;

      const finalizedAuth = await completeSigninWithOrganization(res, configurationSettings);
      login(finalizedAuth.accessToken, finalizedAuth.refreshToken);
      setSelectedOrgId(finalizedAuth.organizationId);
      const gotoParam = searchParams.get('goto');
      if (gotoParam) {
        navigate(gotoParam);
      } else {
        navigate('/');
      }
    } catch {
      setOtpError(`${t('INVALID_MFA_CODE')} ${t('PLEASE_TRY_AGAIN')}`);
    }
  }, [
    configurationSettings,
    mutateAsync,
    otpValue,
    newMfaId,
    mfaId,
    mfaType,
    login,
    navigate,
    searchParams,
    setSelectedOrgId,
    toast,
    t,
  ]);

  useEffect(() => {
    setIsContentReady(false);

    if (isRouteLoading || !mfaId) {
      return;
    }

    const elapsed = Date.now() - mountedAtRef.current;
    const delay = Math.max(0, VERIFY_PANEL_LOADER_MS - elapsed);

    const timer = window.setTimeout(() => {
      setIsContentReady(true);
    }, delay);

    return () => {
      window.clearTimeout(timer);
    };
  }, [isRouteLoading, mfaId]);

  useEffect(() => {
    if (!isRouteLoading && !mfaId) {
      navigate('/login', { replace: true });
    }
  }, [isRouteLoading, mfaId, navigate]);

  useEffect(() => {
    if (
      otpValue.length === otpLength &&
      !isPending &&
      otpValue !== lastVerifiedOtpRef.current
    ) {
      lastVerifiedOtpRef.current = otpValue;
      void onVerify();
    }
  }, [otpValue, otpLength, isPending, onVerify]);

  const onCancel = () => {
    navigate('/login', { replace: true });
  };

  if (!isContentReady) {
    return <AuthPanelLoader />;
  }

  return (
    <div className="mx-auto flex w-full min-w-0 max-w-[438px] flex-col gap-6 sm:gap-8">
      <div className="flex flex-col items-center gap-6 text-center">
        {isEmailMfa && (
          <div className="h-[108px] w-[120px]">
            <img src={emailSentIcon} alt="" className="h-full w-full object-cover" />
          </div>
        )}
        <div className="flex w-full flex-col gap-2">
          <h1 className="text-2xl font-bold text-high-emphasis">
            {t('VERIFY_ITS_YOU', "Verify it's you")}
          </h1>
          <p className="text-sm font-normal text-high-emphasis">
            {isEmailMfa ? (
              <>
                {t(
                  'MFA_EMAIL_VERIFICATION_INSTRUCTION',
                  'Check your email for the verification code and enter it here to login.'
                )}
                {userEmail && <span className="font-semibold"> ({userEmail})</span>}
              </>
            ) : (
              t(
                'MFA_AUTHENTICATOR_VERIFICATION_INSTRUCTION',
                'Open your authenticator app and enter the verification code here to login.'
              )
            )}
          </p>
        </div>
      </div>

      <div className="flex w-full flex-col gap-4">
        {/* <p className="text-sm font-normal text-high-emphasis">{t('PLEASE_ENTER_KEY')}</p> */}
        <div className="flex flex-col gap-2">
          <div className="flex flex-col gap-1">
            <UIOtpInput
              numInputs={otpLength}
              value={otpValue}
              inputStyle={otpError ? '!border-error !text-destructive' : ''}
              onChange={(value) => {
                setOtpValue(value);
                setOtpError('');
              }}
            />
            {otpError && <span className="text-xs text-destructive">{otpError}</span>}
          </div>

          {isEmailMfa && (
            <div className="flex justify-end pt-0.5">
              <button
                type="button"
                disabled={isResendDisabled}
                onClick={handleResendOTP}
                className={cn(
                  'inline-flex items-center gap-1.5 text-sm font-normal transition-colors',
                  isResendDisabled
                    ? 'cursor-not-allowed text-muted-foreground'
                    : 'text-high-emphasis hover:text-primary'
                )}
              >
                <RefreshCw className="h-4 w-4 shrink-0" aria-hidden />
                <span>
                  {t('RESEND_CODE', 'Resend Code')}
                  {isResendDisabled ? ` (${formatResendCountdown(remainingTime)})` : ''}
                </span>
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="mt-2 flex w-full justify-end gap-3">
        <Button variant="outline" className="min-w-[118px]" onClick={onCancel}>
          {t('CANCEL')}
        </Button>
        <Button
          type="button"
          onClick={() => void onVerify()}
          disabled={isPending || otpValue.length < otpLength || !mfaId}
          className="min-w-[118px]"
        >
          {isPending ? t('VERIFYING') : t('VERIFY')}
        </Button>
      </div>
    </div>
  );
}
